import {
  AmountInput,
  Button,
  FormField,
  SkeletonText,
  SwitchField,
} from '@dev-spendesk/grapes';
import { useFormik } from 'formik';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { useNotifications } from 'modules/app/notifications';
import { NotificationType } from 'modules/app/notifications/notification';
import { useModalState } from 'src/core/common/hooks/useModalState';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useUserRoles } from 'src/core/modules/app/hooks/useUserRoles';
import { useFetchCompanyInstantPaymentLimits } from 'src/core/modules/company/hooks';
import { getCurrency } from 'src/core/modules/company/redux/selectors';
import { formatMoney, getCurrencyOptionByKey } from 'src/core/utils/money';

import { DisableInstantPaymentModal } from './DisableInstantPaymentModal';
import { UpdateInstantPaymentLimitsModal } from './UpdateInstantPaymentLimitsModal';

type AmountLimitFormValues = {
  amountLimit: number | null;
};

export const AmountLimitSection = () => {
  const { t } = useTranslation('global');
  const { pushNotif } = useNotifications();
  const { isAccountOwner } = useUserRoles();
  const currency = useSelector(getCurrency);

  const {
    data: instantPaymentLimitsData,
    isLoading: isFetchingInstantPaymentLimits,
    isAmountLimitEnabled,
    disableInstantPaymentAmountLimits,
    enableInstantPaymentAmountLimits,
    updateInstantPaymentAmountLimits,
  } = useFetchCompanyInstantPaymentLimits();

  const pendingActionHandler = useRef<(() => Promise<void>) | null>(null);

  const disableAmountLimitModalState = useModalState({
    initialState: false,
  });
  const updateInstantPaymentLimitsModalState = useModalState({
    initialState: false,
  });

  const openWarningModal = (handler: () => Promise<void>) => {
    pendingActionHandler.current = handler;
    updateInstantPaymentLimitsModalState.open();
  };

  const closeWarningModal = () => {
    formik.setSubmitting(false);
    pendingActionHandler.current = null;
    updateInstantPaymentLimitsModalState.close();
  };

  const handleWarningModalConfirm = async () => {
    formik.setSubmitting(true);
    await pendingActionHandler.current?.();
    closeWarningModal();
  };

  const handleUpdateAmountLimit = async (amountLimit: number) => {
    try {
      await updateInstantPaymentAmountLimits({
        amountLimit,
      });
      pushNotif({
        type: NotificationType.Success,
        message: t(
          'generalSettings.instantPaymentLimits.amountLimitSection.successNotification',
        ),
      });
    } catch {
      pushNotif({
        type: NotificationType.Danger,
        message: t('misc.errors.serverError'),
      });
    }
  };

  const handleDisableAmountLimit = async () => {
    await disableInstantPaymentAmountLimits();
    formik.resetForm();
  };

  const maxAmountLimit = instantPaymentLimitsData?.maxAmountLimit;
  const formattedMaxAmount = maxAmountLimit
    ? formatMoney(maxAmountLimit, currency)
    : '';
  const errorMessage = t(
    'generalSettings.instantPaymentLimits.amountLimitSection.errorMessage',
    {
      amount: formattedMaxAmount,
    },
  );

  const amountLimitSchema = yup.object({
    amountLimit: yup
      .number()
      .nullable()
      .min(0, errorMessage)
      .max(maxAmountLimit ?? 0, errorMessage),
  });

  const formik = useFormik<AmountLimitFormValues>({
    initialValues: {
      amountLimit: instantPaymentLimitsData?.amountLimit ?? null,
    },
    validationSchema: amountLimitSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const { amountLimit } = values;
      if (amountLimit === null) {
        return;
      }
      openWarningModal(() => handleUpdateAmountLimit(amountLimit));
    },
  });

  const handleSwitchChange = () => {
    if (!isAmountLimitEnabled) {
      openWarningModal(async () => {
        await enableInstantPaymentAmountLimits();
      });
      return;
    }

    disableAmountLimitModalState.open();
  };

  const handleDisableModalConfirm = () => {
    disableAmountLimitModalState.close();
    openWarningModal(handleDisableAmountLimit);
  };

  const canEdit = isAccountOwner;

  return (
    <div className="flex flex-col">
      <SwitchField
        id="instantPaymentToggle"
        fit="parent"
        isChecked={isAmountLimitEnabled}
        isDisabled={!canEdit}
        label={t(
          'generalSettings.instantPaymentLimits.amountLimitSection.title',
        )}
        helpText={t(
          'generalSettings.instantPaymentLimits.amountLimitSection.description',
        )}
        onChange={handleSwitchChange}
      />
      {isAmountLimitEnabled && (
        <div className="mt-16 mb-16 flex flex-col gap-24">
          <p className="text-content-secondary-bg-primary body-m">
            {t(
              'generalSettings.instantPaymentLimits.amountLimitSection.helper',
              {
                amount: formattedMaxAmount,
              },
            )}
          </p>
          <form onSubmit={formik.handleSubmit}>
            <div className="flex items-start gap-8">
              {isFetchingInstantPaymentLimits ? (
                <SkeletonText width="200px" />
              ) : (
                <FormField
                  label={t(
                    'generalSettings.instantPaymentLimits.amountLimitSection.title',
                  )}
                  alertMessage={formik.errors.amountLimit}
                >
                  <AmountInput
                    name="amountLimit"
                    fit="parent"
                    value={formik.values.amountLimit}
                    currency={getCurrencyOptionByKey(currency)}
                    isDisabled={!canEdit}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </FormField>
              )}
              {canEdit && (
                <Button
                  className="mt-24"
                  type="submit"
                  variant="secondaryNeutral"
                  text={t(
                    'generalSettings.instantPaymentLimits.amountLimitSection.submitButton',
                  )}
                  isDisabled={
                    !formik.dirty ||
                    formik.isSubmitting ||
                    Object.keys(formik.errors).length > 0
                  }
                  isLoading={formik.isSubmitting}
                />
              )}
            </div>
          </form>
        </div>
      )}
      <DisableInstantPaymentModal
        isOpen={disableAmountLimitModalState.isOpen}
        onClose={disableAmountLimitModalState.close}
        onConfirm={handleDisableModalConfirm}
      />
      <UpdateInstantPaymentLimitsModal
        isOpen={updateInstantPaymentLimitsModalState.isOpen}
        onClose={closeWarningModal}
        onConfirm={handleWarningModalConfirm}
      />
    </div>
  );
};
